import React, {useEffect} from 'react';
  import Chart from 'react-apexcharts';
  import BusinessesInsightsAllInteractions from "./insights/BusinessesInsightsAllInteractions";
  import {useParams} from "react-router-dom";
  import {useApi} from "../../providers/ApiProvider";
  import BusinessesModel from "../../models/BusinessesModel";
  import {useTranslation} from "react-i18next";
  import CardLoading from "../ui/CardLoading";

  function BusinessViewProfileQuality(props) {
      const {t} = useTranslation();
      const params = useParams();
      const apiContext = useApi();
      const businessesModel = new BusinessesModel(apiContext.api);
      const [insights, setInsights] = React.useState({});
      const [profileQuality, setProfileQuality] = React.useState([0, 100]);
      const [loading, setLoading] = React.useState(true);
      const [showDetails, setShowDetails] = React.useState(false);

      useEffect(() => {
          getInsights();
      }, []);

      useEffect(() => {
          let score = 0;
          if (props.locationInfo && props.locationInfo.score && props.locationInfo.score.totalScore) {
              score = Math.round(props.locationInfo.score.totalScore * 100);
          }
          setProfileQuality([score, 100 - score]);
      }, [props.locationInfo]);

      const getInsights = () => {
          setLoading(true);
          const defaultDateEnd = (new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);
          const defaultDateStart = (new Date(Date.now() - (30 + 3) * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);
          businessesModel.insights(params.locationId, defaultDateStart, defaultDateEnd).then((response) => {
              if (response !== false) {
                  setInsights(response);
              }
          }).finally(() => {
              setLoading(false);
          });
      };

      const [donutOptions, setDonutOptions] = React.useState({
          color: "#adb5bd",
          labels: [t('Current'), t('Total')],
          series: profileQuality,
          chart: {
              type: "donut",
              fontFamily: '"Plus Jakarta Sans", "Open Sans", Arial, sans-serif',
              foreColor: "#adb0bb",
          },
          plotOptions: {
              pie: {
                  donut: {
                      size: '89%',
                      background: 'transparent',
                      labels: {
                          show: true,
                          name: {show: true, offsetY: 7},
                          value: {show: false},
                          total: {
                              show: true,
                              color: '#5A6A85',
                              fontSize: '20px',
                              fontWeight: "600",
                              label: profileQuality[0] + ' / 100',
                          },
                      },
                  },
              },
          },
          dataLabels: {enabled: false},
          stroke: {show: false},
          legend: {show: false},
          colors: ["var(--bs-primary)", "#EAEFF4", "var(--bs-secondary)"],
          tooltip: {theme: "dark", fillSeriesColor: false},
      });

      useEffect(() => {
          let donutOptionsNew = {...donutOptions};
          donutOptionsNew.series = profileQuality;
          donutOptionsNew.plotOptions.pie.donut.labels.total.label = profileQuality[0] + ' / 100';
          setDonutOptions(donutOptionsNew);
      }, [profileQuality]);

      const scoreData = props.locationInfo?.score;

      const sectionLabels = {
          title: {icon: 'ti-building', label: t('Unternehmensname'), weight: '30%'},
          categories: {icon: 'ti-category', label: t('Kategorien'), weight: '30%'},
          description: {icon: 'ti-file-text', label: t('Beschreibung'), weight: '20%'},
          address: {icon: 'ti-map-pin', label: t('Adresse'), weight: '5%'},
          location: {icon: 'ti-current-location', label: t('Standort'), weight: '5%'},
          phones: {icon: 'ti-phone', label: t('Telefonnummern'), weight: '3%'},
          website: {icon: 'ti-world', label: t('Website'), weight: '3%'},
          hours: {icon: 'ti-clock', label: t('\u00d6ffnungszeiten'), weight: '2%'},
          services: {icon: 'ti-list-check', label: t('Zusätzliche Informationen'), weight: '2%'},
      };

      const getScoreColor = (score) => {
          if (score >= 0.8) return 'success';
          if (score >= 0.5) return 'warning';
          return 'danger';
      };

      const translateRecommendation = (rec) => {
          let text = rec.text || '';
          if (rec.replacements) {
              for (const [key, value] of Object.entries(rec.replacements)) {
                  text = text.replace(key, value);
              }
          }
          const translations = {
              'Add a business name to your business.': t('F\u00fcgen Sie einen Unternehmensnamen hinzu.'),
              'Business name is longer than 60 characters. Shorten it.': t('Der Unternehmensname ist l\u00e4nger als 60 Zeichen. K\u00fcrzen Sie ihn.'),
              'Add a primary category to your business.': t('F\u00fcgen Sie eine Hauptkategorie hinzu.'),
              'Add more categories to your business.': t('F\u00fcgen Sie weitere Kategorien hinzu.'),
              'Add a description to your business.': t('F\u00fcgen Sie eine Unternehmensbeschreibung hinzu.'),
              'Add an address to your business.': t('F\u00fcgen Sie eine Adresse hinzu.'),
              'Add a precise location to your business.': t('F\u00fcgen Sie eine genaue Position hinzu.'),
              'Add a service area to your business.': t('F\u00fcgen Sie ein Einzugsgebiet hinzu.'),
              'Add a primary phone number to your business.': t('F\u00fcgen Sie eine Telefonnummer hinzu.'),
              'Add more phone numbers to your business.': t('F\u00fcgen Sie weitere Telefonnummern hinzu.'),
              'Add a website to your business.': t('F\u00fcgen Sie eine Website hinzu.'),
              'Add business hours to your business.': t('F\u00fcgen Sie \u00d6ffnungszeiten hinzu.'),
              'Add additional information to your business.': t('F\u00fcgen Sie zus\u00e4tzliche Informationen hinzu.'),
          };
          if (text.includes('Description is shorter than 400 characters')) {
              return t('Die Beschreibung hat weniger als 400 Zeichen. F\u00fcgen Sie mehr Informationen hinzu.');
          }
          if (text.includes('Primary category name should be mentioned')) {
              const match = text.match(/"([^"]+)"/);
              const cat = match ? match[1] : '';
              return t('Erw\u00e4hnen Sie die Hauptkategorie in der Beschreibung: ') + '"' + cat + '"';
          }
          if (text.includes('Additional category name should be mentioned')) {
              const match = text.match(/"([^"]+)"/);
              const cat = match ? match[1] : '';
              return t('Erw\u00e4hnen Sie die Kategorie in der Beschreibung: ') + '"' + cat + '"';
          }
          return translations[text] || text;
      };

      const totalRecommendations = scoreData ? Object.values(scoreData.recommendations || {}).reduce((sum, recs) => sum + (recs?.length || 0), 0) : 0;

      const completedSections = scoreData ? Object.entries(sectionLabels).filter(([key]) => {
          const score = scoreData.scores?.[key] ?? 0;
          const recs = scoreData.recommendations?.[key] || [];
          return score >= 1 && recs.length === 0;
      }) : [];

      const improveSections = scoreData ? Object.entries(sectionLabels).filter(([key]) => {
          const score = scoreData.scores?.[key] ?? 0;
          const recs = scoreData.recommendations?.[key] || [];
          return score < 1 || recs.length > 0;
      }) : [];

      return (
          <>
              <div className="row">
                  <div className="col-md-4 d-flex align-items-stretch">
                      <div className={'card w-100 position-relative overflow-hidden' + (loading ? ' loading' : '')}>
                          <div className="card-body pb-0">
                              <div className="d-flex align-items-end justify-content-around">
                                  <div>
                                      <p className="mb-0 text-center">{t('Profile Quality')}</p>
                                  </div>
                              </div>
                          </div>
                          <Chart type={'donut'} options={donutOptions} series={profileQuality} width={'100%'} height={160} className={'py-4'} />
                          {totalRecommendations > 0 && (
                              <div className="px-3 pb-3 text-center">
                                  <button className="btn btn-sm btn-outline-primary w-100" onClick={() => setShowDetails(!showDetails)}>
                                      <i className={"ti me-1 " + (showDetails ? "ti-chevron-up" : "ti-chevron-down")}></i>
                                      {showDetails ? t('Details ausblenden') : t('{{count}} Verbesserungen m\u00f6glich', {count: totalRecommendations})}
                                  </button>
                              </div>
                          )}
                          {totalRecommendations === 0 && scoreData && (
                              <div className="px-3 pb-3 text-center">
                                  <span className="badge bg-success px-3 py-2"><i className="ti ti-check me-1"></i>{t('Profil vollst\u00e4ndig!')}</span>
                              </div>
                          )}
                          <CardLoading loading={loading} />
                      </div>
                  </div>
                  <div className="col-md-4 d-flex align-items-stretch">
                      <BusinessesInsightsAllInteractions metricLabel={t('Impressions')} label={t('Views for 30 days')} insights={insights} loading={loading} />
                  </div>
                  <div className="col-md-4 d-flex align-items-stretch">
                      <BusinessesInsightsAllInteractions metricLabel={t('Total')} label={t('Interactions for 30 days')} insights={insights} loading={loading} />
                  </div>
              </div>

              {showDetails && scoreData && (
                  <div className="row">
                      {improveSections.length > 0 && (
                          <div className={completedSections.length > 0 ? "col-lg-7" : "col-lg-12"}>
                              <div className="card rounded-2 mb-3 border-warning">
                                  <div className="card-header bg-light-warning border-bottom-0 py-3">
                                      <h6 className="fw-semibold mb-0 text-warning">
                                          <i className="ti ti-alert-triangle me-2"></i>
                                          {t('Verbesserungen')} ({improveSections.length})
                                      </h6>
                                  </div>
                                  <div className="card-body p-3 pt-0">
                                      {improveSections.map(([key, section]) => {
                                          const sectionScore = scoreData.scores?.[key] ?? 0;
                                          const sectionRecs = scoreData.recommendations?.[key] || [];
                                          const percentage = Math.round(sectionScore * 100);
                                          const color = getScoreColor(sectionScore);

                                          return (
                                              <div key={key} className="mb-3 pt-3 border-bottom pb-3">
                                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                                      <div className="d-flex align-items-center gap-2">
                                                          <i className={"ti " + section.icon + " text-" + color + " fs-5"}></i>
                                                          <span className="fw-semibold">{section.label}</span>
                                                          <span className="text-muted fs-2">({section.weight})</span>
                                                      </div>
                                                      <span className={"badge bg-" + color}>{percentage}%</span>
                                                  </div>
                                                  <div className="progress mb-2" style={{height: '5px'}}>
                                                      <div className={"progress-bar bg-" + color} role="progressbar" style={{width: percentage + '%'}}></div>
                                                  </div>
                                                  {sectionRecs.length > 0 && (
                                                      <div className="mt-2">
                                                          {sectionRecs.map((rec, i) => (
                                                              <div key={key + '-rec-' + i} className="d-flex align-items-start gap-2 mb-2 ps-2" style={{borderLeft: '3px solid var(--bs-warning)'}}>
                                                                  <div>
                                                                      <span className="fs-2">{translateRecommendation(rec)}</span>
                                                                  </div>
                                                              </div>
                                                          ))}
                                                      </div>
                                                  )}
                                              </div>
                                          );
                                      })}
                                  </div>
                              </div>
                          </div>
                      )}

                      {completedSections.length > 0 && (
                          <div className={improveSections.length > 0 ? "col-lg-5" : "col-lg-12"}>
                              <div className="card rounded-2 mb-3 border-success">
                                  <div className="card-header bg-light-success border-bottom-0 py-3">
                                      <h6 className="fw-semibold mb-0 text-success">
                                          <i className="ti ti-circle-check me-2"></i>
                                          {t('Erledigt')} ({completedSections.length})
                                      </h6>
                                  </div>
                                  <div className="card-body p-3 pt-0">
                                      {completedSections.map(([key, section]) => {
                                          const percentage = Math.round((scoreData.scores?.[key] ?? 0) * 100);
                                          return (
                                              <div key={key} className="d-flex align-items-center gap-2 py-2 border-bottom">
                                                  <i className={"ti " + section.icon + " text-success fs-5"}></i>
                                                  <span className="fw-semibold fs-3">{section.label}</span>
                                                  <span className="text-muted fs-2">({section.weight})</span>
                                                  <span className="badge bg-success ms-auto">{percentage}%</span>
                                              </div>
                                          );
                                      })}
                                  </div>
                              </div>
                          </div>
                      )}
                  </div>
              )}
          </>
      );
  }

  export default BusinessViewProfileQuality;
  